// apps/admin/src/routes/users/UsersList.tsx
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Plus, Search, User, MoreHorizontal, ShieldAlert, CheckCircle, XCircle, Eye, Pencil, Trash2 } from "lucide-react";
import { useState, useMemo } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";

// Convex ID type helper
type Id<T extends string> = string & { __tableName: T };
import {
  Input,
  Button,
  Badge,
  Avatar,
  AvatarImage,
  AvatarFallback,
  Skeleton,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  useToast,
} from "@jourfx/ui";
import { PageHeader } from "../../components/PageHeader";
import { StatusBadge, type StatusType } from "../../components/StatusBadge";
import { EmptyState } from "../../components/EmptyState";
import { useSession } from "../../lib/auth";

// User type from authManagement query
interface AuthUser {
  _id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  role: string;
  status: string;
  organizationId: string;
  createdAt: number;
  auth: {
    emailVerified: boolean;
  } | null;
  sessionCount: number;
}

function getUserRoleLabel(
  t: (key: string) => string,
  role: string
): string {
  switch (role) {
    case "administrator":
      return t("roles.administrator");
    case "gutachter":
      return t("roles.gutachter");
    case "assistent":
      return t("roles.assistent");
    case "versicherter":
      return t("roles.versicherter");
    case "super_admin":
      return t("roles.super_admin");
    default:
      return role;
  }
}

export function UsersList() {
  const { t } = useTranslation('users');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated, isPending } = useSession();
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<AuthUser | null>(null);

  const removeUser = useMutation(api.admin.users.remove);

  const handleDeleteClick = (user: AuthUser, e: React.MouseEvent) => {
    e.stopPropagation();
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await removeUser({ id: userToDelete._id as Id<"users"> });
      toast({
        title: t('deleteSuccess.title'),
        description: t('deleteSuccess.description'),
      });
    } catch (error) {
      toast({
        title: tc('error.title'),
        description: error instanceof Error ? error.message : tc('error.unknown'),
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleEditClick = (userId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    // Navigate to detail page - editing is done there
    navigate({ to: "/users/$userId", params: { userId } });
  };

  const queryArgs = isAuthenticated
    ? {
        status: statusFilter !== "all" ? statusFilter : undefined,
        limit: 50,
      }
    : "skip";

  const result = useQuery(
    api.admin.authManagement.listAuthUsers,
    queryArgs as any
  ) as { users: AuthUser[]; hasMore: boolean } | undefined | null;

  // Client-side filtering for search and role since listAuthUsers doesn't have these params
  const users = useMemo(() => {
    if (!result?.users) return [];

    let filtered = result.users as AuthUser[];

    // Filter by role
    if (roleFilter !== "all") {
      filtered = filtered.filter((u) => u.role === roleFilter);
    }

    // Filter by search term
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          u.name.toLowerCase().includes(searchLower) ||
          u.email.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [result?.users, roleFilter, search]);

  if (isPending || result === undefined) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-10 w-36" />
        </div>
        <div className="flex flex-col gap-4 sm:flex-row">
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 w-40" />
          <Skeleton className="h-10 w-40" />
        </div>
        <div className="rounded-lg border bg-card overflow-hidden">
          <div className="divide-y divide-border">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4 px-6 py-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-6 w-16" />
                <Skeleton className="h-8 w-8" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <ShieldAlert className="h-12 w-12 text-warning" />
        <h2 className="text-xl font-semibold text-foreground">{tc('auth.loginRequired')}</h2>
        <p className="text-muted-foreground text-center max-w-md">
          {tc('auth.loginRequiredMessage')}
        </p>
        <Link
          to="/login"
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {tc('auth.toLogin')}
        </Link>
      </div>
    );
  }

  if (result === null) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <ShieldAlert className="h-12 w-12 text-warning" />
        <h2 className="text-xl font-semibold text-foreground">{tc('error.title')}</h2>
        <p className="text-muted-foreground text-center max-w-md">
          {tc('error.unknown')}
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {tc('actions.back')}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title={t('title')}
        description={t('subtitle')}
        actions={
          <>
            <Button asChild variant="outline">
              <Link to="/users/import">
                <Plus className="mr-2 h-4 w-4" />
                {t('import.action')}
              </Link>
            </Button>
            <Button asChild>
              <Link to="/users/new">
                <Plus className="mr-2 h-4 w-4" />
                {t('addUser')}
              </Link>
            </Button>
          </>
        }
      />

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder={t('searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <option value="all">{t('filters.allRoles')}</option>
          <option value="administrator">{t('roles.administrator')}</option>
          <option value="gutachter">{t('roles.gutachter')}</option>
          <option value="assistent">{t('roles.assistent')}</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <option value="all">{tc('table.allStatuses')}</option>
          <option value="active">{tc('status.active')}</option>
          <option value="pending">{tc('status.pending')}</option>
          <option value="locked">{tc('status.locked')}</option>
          <option value="inactive">{tc('status.inactive')}</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border bg-card">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('table.user')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('table.organization')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('table.role')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('table.emailVerified')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('table.sessions')}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {tc('table.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users.map((user) => (
              <tr
                key={user._id}
                className="group transition-colors hover:bg-muted/50 cursor-pointer"
                onClick={() => navigate({ to: "/users/$userId", params: { userId: user._id } })}
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      {user.avatarUrl && (
                        <AvatarImage src={user.avatarUrl} alt={user.name} />
                      )}
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {user.name.slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-medium text-foreground">{user.name}</div>
                      <div className="text-sm text-muted-foreground">{user.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-muted-foreground">
                  -
                </td>
                <td className="px-6 py-4">
                  <Badge variant="secondary">
                    {getUserRoleLabel(t, user.role)}
                  </Badge>
                </td>
                <td className="px-6 py-4">
                  <StatusBadge status={user.status as StatusType} />
                </td>
                {/* Email Verified */}
                <td className="px-6 py-4">
                  {user.auth?.emailVerified ? (
                    <span className="inline-flex items-center gap-1.5 text-success">
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-xs font-medium">{t('verified')}</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                      <XCircle className="h-4 w-4" />
                      <span className="text-xs">{t('unverified')}</span>
                    </span>
                  )}
                </td>
                {/* Active Sessions */}
                <td className="px-6 py-4">
                  {user.sessionCount > 0 ? (
                    <span className="inline-flex items-center gap-1.5 tabular-nums">
                      <span className="font-medium text-foreground">{user.sessionCount}</span>
                      <span className="text-xs text-muted-foreground">{t('activeSessions')}</span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </td>
                <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Menü öffnen</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <Link to="/users/$userId" params={{ userId: user._id }}>
                          <Eye className="mr-2 h-4 w-4" />
                          {tc('actions.view')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => handleEditClick(user._id, e)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        {tc('actions.edit')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={(e) => handleDeleteClick(user, e)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {tc('actions.delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {users.length === 0 && (
        <EmptyState
          icon={User}
          title={t('empty.title')}
          description={t('empty.message')}
          action={{
            label: t('addUser'),
            onClick: () => navigate({ to: "/users/new" }),
            icon: Plus,
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteDialog.description', { name: userToDelete?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tc('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {tc('actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
